import React from "react";
import styled from "styled-components";
import { match } from "ts-pattern";
import { useQuery } from "react-query";
import {
	Button,
	Icon,
	Colors,
	Classes,
	IconName,
	Divider,
	ButtonGroup,
} from "@blueprintjs/core";
import { UrlExternalReferenceT } from "ui-io/task-io";
import { AssetOptions } from "ui-io/assets-io";
import { Text, ErrorState } from "ui-components";
import useSession from "ui-io/use-session";

import { ActiveAttachmentT } from ".";
import {
	formatBytes,
	getIconForContentType,
	getValidContentType,
} from "../../../utils/attachment-utilities";
import { getAssetInfoQuery } from "ui-io/queries/assets/get-asset-info-query";

type AttachmentProps = {
	attachment: any;
	assetOptions?: AssetOptions;
	isUsersComment?: boolean;
	handleToggleIsAttachmentOpen?: (nextAttachment: ActiveAttachmentT) => void;
	onDelete?: (attachmentId: string | undefined) => void;
	uploadedUser?: string;
	uploadTimeStamp: number;
	deleteEnabled?: boolean;
	isDeleteButton?: boolean;
};

const Attachment = (props: AttachmentProps) => {
	const {
		attachment,
		assetOptions = {},
		isUsersComment,
		handleToggleIsAttachmentOpen,
		onDelete,
		uploadedUser,
		uploadTimeStamp,
		deleteEnabled,
		isDeleteButton = true,
	} = props;

	const connection = useQuery(
		getAssetInfoQuery(attachment.url_reference, assetOptions)
	);
	const AttachmentWrapper = isUsersComment ? RightAttachment : LeftAttachment;
	const currentTimeStamp = Math.round(+new Date() / 1000);
	const { session } = useSession();
	const username = session?.username ?? "";
	const FIVE_MINS_IN_TIMESTAMP = 300;
	const enableDeleteDefault =
		username === uploadedUser &&
		currentTimeStamp - uploadTimeStamp <= FIVE_MINS_IN_TIMESTAMP;
	const enableDelete = enableDeleteDefault || deleteEnabled;
	const IconWrapper = isUsersComment ? RightIconWrapper : LeftIconWrapper;
	const downloadFromURL = (url: string, name: string = "download.jpg") => {
		const link = document.createElement("a");
		fetch(url)
			.then((res) => res.blob())
			.then((blob) => {
				const blobUrl = URL.createObjectURL(blob);
				link.href = blobUrl;
				link.download = name;
				link.click();
				URL.revokeObjectURL(blobUrl);
			});
	};
	return (
		<AttachmentWrapper>
			{match(connection)
				.with({ status: "error" }, ({ error }) => <ErrorState error={error} />)
				.with({ status: "loading" }, { status: "idle" }, () => {
					return (
						<>
							<IconWrapper className={Classes.SKELETON}>
								<Icon icon="document" />
							</IconWrapper>
							<div className={Classes.SKELETON}>{attachment.description}</div>
						</>
					);
				})
				.with({ status: "success" }, ({ data }) => {
					const { assetProperties, getAssetUrlQuery } = data;
					const contentType = getValidContentType(assetProperties.content_type);
					const icon = getIconForContentType(contentType);
					const altText =
						attachment.description ??
						attachment.name ??
						assetProperties.filename;
					return (
						<>
							<IconWrapper>
								{/* <Icon icon={icon[index] as IconName} /> */}
								<Icon icon={icon as IconName} />
							</IconWrapper>
							<TextContainer>
								<DescriptionText title={attachment?.url_reference?.description}>
									{attachment?.url_reference?.description}
								</DescriptionText>
								<TinyText>{formatBytes(assetProperties?.filesize)}</TinyText>
							</TextContainer>
							<Divider style={{ borderColor: Colors.GRAY4 }} />
							<ButtonGroup style={{ alignSelf: "center" }}>
								<Button
									icon="download"
									small
									minimal
									onClick={() => {
										const { url } = attachment.url_reference;
										const name = attachment.asset_properties.filename;
										if (url) {
											downloadFromURL(url, name);
										}
									}}
								/>
								{!!handleToggleIsAttachmentOpen ? (
									<Button
										icon="eye-open"
										small
										minimal
										onClick={() => {
											handleToggleIsAttachmentOpen({
												attachment,
												icon,
												altText,
												getAssetUrlQuery,
												assetProperties,
												contentType,
											});
										}}
									/>
								) : null}
								{isDeleteButton && (
									<Button
										icon="trash"
										minimal
										small
										onClick={() =>
											onDelete && onDelete(attachment?.url_reference?.name)
										}
										disabled={!enableDelete}
									/>
								)}
							</ButtonGroup>
						</>
					);
				})
				.exhaustive()}
		</AttachmentWrapper>
	);
};

export default Attachment;

const DefaultAttachmentWrapper = styled.div`
  padding: 14px;
  border-radius: 4px;
  width: 100%;
  display: flex;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 6px;
`;

const LeftAttachment = styled(DefaultAttachmentWrapper)`
  border: ${(props) =>
		`1px solid ${props.theme.darkMode ? Colors.DARK_GRAY4 : Colors.LIGHT_GRAY4
		}`};
  max-width: 500px;
`;

const TinyText = styled.div`
  font-size: 10px;
`;

const DescriptionText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RightAttachment = styled(DefaultAttachmentWrapper)`
  background-color: ${(props) => {
		return props.theme.darkMode ? Colors.DARK_GRAY4 : Colors.LIGHT_GRAY3;
	}};
`;

const DefaultIconWrapper = styled.div`
  padding: 6px;
  border-radius: 4px;
  align-self: flex-start;
  margin-right: 6px;
`;
const LeftIconWrapper = styled(DefaultIconWrapper)`
  background-color: ${(props) => {
		return props.theme.darkMode ? Colors.DARK_GRAY3 : Colors.LIGHT_GRAY5;
	}};
  color: ${(props) => {
		return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY3;
	}};
`;

const RightIconWrapper = styled(DefaultIconWrapper)`
  background-color: ${(props) => {
		return props.theme.darkMode ? Colors.LIGHT_GRAY3 : Colors.LIGHT_GRAY5;
	}};
  color: ${(props) => {
		return props.theme.darkMode ? Colors.DARK_GRAY5 : Colors.DARK_GRAY3;
	}};
`;
